import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  CustomColumnField,
  DimensionField,
  GroupingField,
  MeasureField,
  MetricField,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectAutoShowItemOptionsId } from "../../../../../store/selectors/currentReportSelectors";
import {
  selectBuilderDetailedView,
  selectConditions,
  selectReportType,
} from "../../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { createCurrentReportAction } from "../../../../../store/utils/storeUtils";
import { getDimensionsAllowedInConditions } from "../../../../../utilities/conditionDataTypes.ts";
import { isCustomColumn, isDimensionBased, isMeasure, isMetric } from "../../../../../utilities/fieldsHelper.ts";
import { getDimensionFields, isMeasureConfigurationValid } from "../../../../../utilities/isConfigurationValid.ts";
import type { FieldItemProps } from "../../../../viewer/common/fields/area-fields/areaFiledItem.types.ts";
import {
  getDimensionIconSet,
  getMeasureIconSet,
} from "../../../../viewer/common/fields/field-captions/fieldsCaptionIconsProvider.tsx";
import MeasureItem from "../../../../viewer/common/fields/MeasureItem.tsx";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import { formatMeasureCaption } from "../../../../viewer/common/values/helper.tsx";
import { formatDimensionInFieldsCaption } from "../../../utils/formatDimensionFieldCaptions";
import DimensionItem from "../../fields/DimensionItem";

interface FieldsDropContainerReturnType {
  createItem: (props: FieldItemProps<ReportField>) => JSX.Element;
  handleRemove: () => void;
  closePopup: () => void;
  handleUpdateConfiguration: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  handleDimensionFieldCustomLabelChange: (name: string | undefined) => void;
  moveItem: (item: ReportField, newIndex: number) => void;
  addItem: (item: ReportField, index: number) => void;
  updateItemConfig: (field: ReportField, change: Partial<FieldConfiguration>) => void;
  removeItem: (item: ReportField) => void;
  anchorEl: HTMLElement | null;
  currentField: ReportField | undefined;
  dimensionFields: DimensionField[];
  conditions: ConditionField[];
  readonlyMode?: boolean;
}

interface FieldsDropContainerOptions {
  readonlyMode?: boolean;
  grouping?: GroupingField[];
  freezedColumns?: string[];
  showGroupedColumns?: boolean;
  showDateGroupingCaption?: boolean;
}

const useFieldsDropContainer = (
  fields: ReportField[],
  {
    readonlyMode = false,
    grouping,
    freezedColumns,
    showGroupedColumns,
    showDateGroupingCaption = true,
  }: FieldsDropContainerOptions = {}
): FieldsDropContainerReturnType => {
  const dispatch = useAppDispatch();
  const detailedView = useSelector(selectBuilderDetailedView);

  const reportType = useSelector(selectReportType) ?? undefined;
  const conditionsAreaValues = useSelector(selectConditions);
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);
  const storedDimensions = useSelector(selectDimensions);
  const dimensionFields = useMemo(() => getDimensionFields(fields), [fields]);
  const onSetAutoShowItemOptionsId = useCallback(
    (id?: string) => {
      dispatch(createCurrentReportAction("setAutoShowItemOptionsId", id));
    },
    [dispatch]
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [currentFieldId, setCurrentFieldId] = useState<string>();
  const currentField = useMemo(() => {
    if (!currentFieldId) {
      return undefined;
    }
    return fields.find((f) => f.config.guid === currentFieldId);
  }, [currentFieldId, fields]);

  const dimensions = useMemo(() => {
    return getDimensionsAllowedInConditions(storedDimensions, reportType).sort((a, b) =>
      a.caption.localeCompare(b.caption)
    );
  }, [storedDimensions, reportType]);

  const dimensionsRef = useRef(dimensions);
  useEffect(() => {
    dimensionsRef.current = dimensions;
  }, [dimensions]);

  const showFieldOptions = useCallback((settings: ShowFieldOptionsSettings<ReportField>) => {
    setAnchorEl(settings.ref);
    setCurrentFieldId(settings.field.config.guid);
  }, []);

  const measureCaptionColor = useCallback(
    (value: MeasureField | MetricField | CustomColumnField) => {
      const valid = isMeasureConfigurationValid(conditionsAreaValues, dimensionFields, dimensionsRef.current, value);
      return valid.valid ? undefined : "error.main";
    },
    [conditionsAreaValues, dimensionFields]
  );

  const handleMeasureFormatCaption = useCallback(
    (measure: MeasureField | MetricField | CustomColumnField) => {
      return formatMeasureCaption(
        measure,
        measureCaptionColor(measure),
        getMeasureIconSet(measure, freezedColumns ?? [])
      );
    },
    [measureCaptionColor, freezedColumns]
  );

  const handleDimensionFormatCaption = useCallback(
    (dimension: DimensionField) => {
      return formatDimensionInFieldsCaption(
        dimension,
        getDimensionIconSet(dimension, freezedColumns ?? [], grouping ?? [], showGroupedColumns ?? false),
        showDateGroupingCaption
      );
    },
    [showGroupedColumns, grouping, freezedColumns, showDateGroupingCaption]
  );

  const removeItem = useCallback(
    (item: ReportField) => {
      dispatch(fieldsStateActions.removeField(item));
    },
    [dispatch]
  );

  const moveItem = useCallback(
    (item: ReportField, newIndex: number) => {
      dispatch(fieldsStateActions.moveField({ field: item, newIndex }));
    },
    [dispatch]
  );

  const addItem = useCallback(
    (item: ReportField, index: number) => {
      dispatch(fieldsStateActions.addField({ field: item, index }));
    },
    [dispatch]
  );

  const updateItemConfig = useCallback(
    (field: ReportField, change: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateFieldConfig({ field, changes: change }));
    },
    [dispatch]
  );

  const createItem = useCallback(
    (props: FieldItemProps<ReportField>) => {
      if (isMeasure(props.field) || isMetric(props.field) || isCustomColumn(props.field)) {
        const field = props.field;
        return (
          <MeasureItem
            detailedView={detailedView}
            field={field}
            dimensions={dimensions}
            globalConditions={conditionsAreaValues}
            formatCaption={handleMeasureFormatCaption}
            canBeRemoved={true}
            onRemoveItem={removeItem}
            updateMeasure={updateItemConfig}
            onShowOptions={showFieldOptions}
            canToggleParameter={true}
            isReadonlyCustomLabel={true}
            autoShowItemOptionsId={autoShowItemOptionsId}
            onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
            readonly={readonlyMode}
          />
        );
      }
      return (
        <DimensionItem
          detailedView={detailedView}
          field={props.field}
          dimensions={dimensions}
          globalConditions={conditionsAreaValues}
          canBeRemoved={true}
          onRemoveItem={removeItem}
          formatCaption={handleDimensionFormatCaption}
          updateField={updateItemConfig}
          onShowOptions={showFieldOptions}
          autoShowItemOptionsId={autoShowItemOptionsId}
          onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
          readonly={readonlyMode}
        />
      );
    },
    [
      detailedView,
      conditionsAreaValues,
      handleMeasureFormatCaption,
      showFieldOptions,
      removeItem,
      updateItemConfig,
      handleDimensionFormatCaption,
      autoShowItemOptionsId,
      onSetAutoShowItemOptionsId,
      dimensions,
      readonlyMode,
    ]
  );

  const handleRemove = useCallback(() => {
    setAnchorEl(null);
    if (currentField) {
      removeItem(currentField);
    }
  }, [currentField, removeItem]);

  const closePopup = useCallback(() => {
    setCurrentFieldId(undefined);
    setAnchorEl(null);
  }, []);

  const handleUpdateConfiguration = useCallback(
    (changes: Partial<FieldConfiguration>, causeClosing: boolean) => {
      if (!currentField) {
        return;
      }
      updateItemConfig(currentField, changes);
      if (causeClosing) {
        setAnchorEl(null);
      }
    },
    [currentField, updateItemConfig]
  );

  const handleDimensionFieldCustomLabelChange = useCallback(
    (name: string | undefined) => {
      if (!isDimensionBased(currentField)) {
        return;
      }
      updateItemConfig(currentField, { customLabel: name });
    },
    [currentField, updateItemConfig]
  );

  return {
    createItem,
    handleRemove,
    closePopup,
    handleUpdateConfiguration,
    handleDimensionFieldCustomLabelChange,
    moveItem,
    addItem,
    updateItemConfig,
    anchorEl,
    currentField,
    dimensionFields,
    removeItem,
    conditions: conditionsAreaValues,
  };
};

export default useFieldsDropContainer;
